/**
 * Codes de départ / solutions et spécification de l'exercice pré-créé « Time.deltaTime ».
 * `DELTATIME_EXERCISE_SPEC` est inséré en base comme exercice pré-créé (pas généré par IA),
 * voir shared/src/constants/prebuilt-exercises.ts.
 */

import type { SimFps } from './runtime';

export const DELTATIME_STARTER_CODE = `using UnityEngine;

// Déplace le vaisseau avec les flèches (ou ZQSD / WASD).
// Objectif : la MÊME vitesse à 5, 30 et 60 images par seconde.
public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f; // en unités par seconde

    void Update()
    {
        // TODO 1 : lire les entrées (-1, 0 ou 1 sur chaque axe)
        // float h = Input.GetAxisRaw("Horizontal");
        // float v = Input.GetAxisRaw("Vertical");

        // TODO 2 : construire la direction à partir de h et v
        //          (un Vector3, normalisé pour que la diagonale ne soit pas plus rapide)

        // TODO 3 : déplacer transform.position à la vitesse "speed"
        //          -> la même vitesse quelle que soit la cadence d'images !
    }
}
`;

export const DELTATIME_REFERENCE_SOLUTION = `using UnityEngine;

public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f;

    void Update()
    {
        float h = Input.GetAxisRaw("Horizontal");
        float v = Input.GetAxisRaw("Vertical");
        Vector3 direction = new Vector3(h, v, 0f).normalized;
        transform.position += direction * speed * Time.deltaTime;
    }
}
`;

/** Solution « naïve » : bouge, mais 12× plus vite à 60 FPS qu'à 5 FPS. Sert aux tests et à la démo. */
export const DELTATIME_NAIVE_SOLUTION = `using UnityEngine;

public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f;

    void Update()
    {
        float h = Input.GetAxisRaw("Horizontal");
        float v = Input.GetAxisRaw("Vertical");
        Vector3 direction = new Vector3(h, v, 0f).normalized;
        transform.position += direction * speed;
    }
}
`;

/** Variante correcte avec `transform.Translate`. */
export const DELTATIME_TRANSLATE_SOLUTION = `using UnityEngine;

public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f;

    void Update()
    {
        float h = Input.GetAxisRaw("Horizontal");
        float v = Input.GetAxisRaw("Vertical");
        Vector3 direction = new Vector3(h, v, 0f).normalized;
        transform.Translate(direction * speed * Time.deltaTime);
    }
}
`;

export interface DeltaTimeExerciseSpec {
  /** Identifiant stable proposé pour la ligne `exercises`. */
  id: string;
  title: string;
  /** Énoncé en markdown (FR). */
  description: string;
  learningMode: 'unity_csharp';
  programmingLanguage: 'csharp';
  difficulty: 'beginner';
  /** Valeurs de `ConceptCategory` (shared). */
  concepts: string[];
  /** Nœud recommandé pour le rattachement — ⚠️ à arbitrer par Mourdjen (cf. README / journal G1). */
  recommendedNodeId: string;
  alternativeNodeId: string;
  estimatedTimeMinutes: number;
  learningOutcomes: string[];
  successCriteria: string[];
  hints: string[];
  startingCode: string;
  expectedSolution: string;
  /** Paramètres à passer à `verifyFrameIndependence`. */
  verify: { seconds: number; tolerance: number; expectedPerSecond: number };
  fpsOptions: SimFps[];
}

export const DELTATIME_EXERCISE_SPEC: DeltaTimeExerciseSpec = {
  id: 'sim-deltatime-ship',
  title: 'Simulateur : la même vitesse à 5, 30 et 60 FPS (Time.deltaTime)',
  description: `## Le problème

Ton vaisseau doit se déplacer avec les flèches (ou ZQSD / WASD) à **5 unités par seconde**.

Unity appelle \`Update()\` **une fois par image**. Sur une machine rapide, c'est 60 fois par seconde ; sur une machine lente ou un téléphone, parfois 5. Si tu avances de \`speed\` **à chaque image**, ton vaisseau va 12 fois plus vite à 60 FPS qu'à 5 FPS — et ton jeu devient injouable selon l'ordinateur.

## Ce que tu dois faire

1. Lire les entrées : \`Input.GetAxisRaw("Horizontal")\` et \`Input.GetAxisRaw("Vertical")\` renvoient \`-1\`, \`0\` ou \`1\`.
2. Construire une direction avec \`new Vector3(h, v, 0f)\`, puis la **normaliser** (\`.normalized\`) pour que la diagonale ne soit pas plus rapide.
3. Déplacer \`transform.position\` à la vitesse \`speed\`, **en unités par seconde**, quelle que soit la cadence d'images.

## Pour tester

Appuie sur **Play**, bouge le vaisseau, puis change la cadence (**5 / 30 / 60 FPS**) et observe le HUD « distance par seconde ». Quand le nombre reste le même aux trois cadences, tu as compris \`Time.deltaTime\`.

> Le simulateur ne comprend qu'une petite partie de C#/Unity (celle dont tu as besoin ici). Si tu utilises autre chose, il te le dira et tu pourras demander pourquoi au tuteur.`,
  learningMode: 'unity_csharp',
  programmingLanguage: 'csharp',
  difficulty: 'beginner',
  concepts: ['monobehaviour_lifecycle', 'gameobject_manipulation', 'vector_math'],
  recommendedNodeId: 'monobehaviour_lifecycle',
  alternativeNodeId: 'gameobject_transform',
  estimatedTimeMinutes: 20,
  learningOutcomes: [
    'Comprendre que Update() est appelé une fois par image et que la cadence varie selon la machine.',
    'Utiliser Time.deltaTime pour exprimer un déplacement en unités par seconde.',
    'Lire des entrées clavier avec Input.GetAxisRaw et construire un Vector3 de direction normalisé.',
    'Modifier transform.position (et savoir pourquoi transform.position.x = … est refusé par C#).',
  ],
  successCriteria: [
    'Le vaisseau se déplace quand on appuie sur les flèches (ou ZQSD / WASD).',
    'La distance parcourue par seconde est identique (±10 %) à 5, 30 et 60 FPS.',
    'La vitesse mesurée vaut ≈ 5 unités par seconde (la valeur du champ speed).',
    'En diagonale, la vitesse reste 5 u/s (direction normalisée), pas 7.',
  ],
  hints: [
    'Time.deltaTime est le temps écoulé depuis l\'image précédente, en secondes : environ 0.2 à 5 FPS, 0.0167 à 60 FPS.',
    'Vitesse (u/s) × temps (s) = distance (u). Que faut-il multiplier par quoi ?',
    'Compare le HUD « distance / s » à 5 FPS et à 60 FPS : si le rapport vaut 12, il manque quelque chose.',
    'La ligne finale tient en une instruction : transform.position += direction * speed * Time.deltaTime;',
  ],
  startingCode: DELTATIME_STARTER_CODE,
  expectedSolution: DELTATIME_REFERENCE_SOLUTION,
  verify: { seconds: 3, tolerance: 0.1, expectedPerSecond: 5 },
  fpsOptions: [5, 30, 60],
};
